/** Inline stroke icons in `currentColor` (docs/02 §7: no emoji, no icon font). */

export function CalendarIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

export function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function BinIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" />
    </svg>
  );
}

export function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** A menu of more actions: three dots one above another. */
export function MoreIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="5" r="1.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** A change: a pencil, after Lucide's (ISC licence). */
export function PencilIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21.17 6.81a1 1 0 0 0-3.98-3.98L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5zM15 5l4 4" />
    </svg>
  );
}

/** Filled in from elsewhere: two arrows round, after Lucide's refresh (ISC licence). */
export function SyncIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8M21 3v5h-5M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16M8 16H3v5" />
    </svg>
  );
}

/** Where from and where to: two places and the way between, after Lucide's route (ISC licence). */
export function RouteIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  );
}

/** Open or closed: a chevron down, turned up when what it closes is open. */
export function ChevronIcon({ open, size = 16 }: { open: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={open ? 'chevron chevron-open' : 'chevron'}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** A ticket to add: a ticket with a plus, after Lucide's ticket-plus (ISC licence). */
export function TicketPlusIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2ZM9 12h6M12 9v6" />
    </svg>
  );
}

/** Another way instead: two arrows each way, after Lucide's arrow-right-left (ISC licence). */
export function SwapIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16" />
    </svg>
  );
}

/** Something happened to a booking: a warning triangle, after Lucide's triangle-alert (ISC). */
export function AlertIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3ZM12 9v4M12 17h.01" />
    </svg>
  );
}

/** A line drawn with Lucide's strokes, `size` square. */
function Stroked({ size, d }: { size: number; d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

/** A branch parting from a line, after Lucide's git-branch (ISC licence). */
export function BranchIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 9a9 9 0 0 1-9 9"
    />
  );
}

/** Food: a fork and a knife, after Lucide's utensils (ISC licence). */
export function FoodIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"
    />
  );
}

/** A photo taken, after Lucide's camera (ISC licence). */
export function CameraIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3zM12 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
    />
  );
}

/** Things to do, by their ticket, after Lucide's ticket (ISC licence). */
export function TicketIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2ZM13 5v2M13 17v2M13 11v2"
    />
  );
}

/** A bed, for a night's stay, after Lucide's bed-double (ISC licence). */
export function BedIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4M12 4v6M2 18h20"
    />
  );
}

/** The days one by one, after Lucide's calendar-days (ISC licence). */
export function DayPlanIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M8 2v4M16 2v4M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"
    />
  );
}

/** The plan, a folded map, after Lucide's map (ISC licence). */
export function MapIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0zM15 5.764v15M9 3.236v15"
    />
  );
}

/** People together, after Lucide's users (ISC licence). */
export function PeopleIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
    />
  );
}

/** Money spent on a day, a wallet, after Lucide's wallet (ISC licence). */
export function WalletIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"
    />
  );
}

/** The countries, a globe, after Lucide's globe (ISC licence). */
export function GlobeIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20M2 12h20"
    />
  );
}

/** Papers, after Lucide's file-text (ISC licence). */
export function DocumentIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7zM14 2v4a2 2 0 0 0 2 2h4M10 9H8M16 13H8M16 17H8"
    />
  );
}

/** The journal, an open book, after Lucide's book-open (ISC licence). */
export function JournalIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M12 7v14M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"
    />
  );
}

/** A search, a magnifying glass, after Lucide's search (ISC licence). */
export function SearchIcon({ size = 16 }: { size?: number }) {
  return <Stroked size={size} d="M21 21l-4.34-4.34M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" />;
}

/** The light theme, a sun, after Lucide's sun (ISC licence). */
export function SunIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked
      size={size}
      d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"
    />
  );
}

/** The dark theme, a moon, after Lucide's moon (ISC licence). */
export function MoonIcon({ size = 16 }: { size?: number }) {
  return <Stroked size={size} d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />;
}

/** More said when pointed at: an `i` in a ring, after Lucide's info (ISC licence). */
export function InfoIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked size={size} d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01" />
  );
}

/** The menu: three lines in a ring. */
export function MenuIcon({ size = 16 }: { size?: number }) {
  return (
    <Stroked size={size} d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 9h8M8 12h8M8 15h8" />
  );
}
